import React, { useState } from "react";
import { toast } from "sonner";
import {
  Flame, Beef, Users, Sandwich, Soup, Drumstick, Plus, Check, ArrowRight, ListPlus,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useScenarios, trackEvent } from "@/lib/api";
import { useOrderList } from "@/context/OrderListContext";
import { formatPrice } from "@/lib/site";
import SectionHeading from "@/components/common/SectionHeading";
import MangalPlanner from "@/components/home/MangalPlanner";

const SCENARIO_ICONS = {
  flame: Flame, beef: Beef, users: Users, sandwich: Sandwich, soup: Soup, drumstick: Drumstick,
};

const ScenarioTile = ({ scenario, onOpen }) => {
  const Icon = SCENARIO_ICONS[scenario.icon] || Flame;
  const cover = scenario.products?.[0]?.image;
  const isMangal = scenario.key === "mangal";
  return (
    <button
      data-testid={`scenario-tile-${scenario.key}`}
      onClick={() => onOpen(scenario)}
      className="group relative aspect-[4/3] overflow-hidden border border-white/5 text-left"
    >
      {cover && (
        <img
          src={cover}
          alt={scenario.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20 transition-colors group-hover:from-bordo/85" />
      <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-6">
        <span className="flex h-11 w-11 items-center justify-center border border-copper/60 bg-black/30 backdrop-blur-sm">
          <Icon className="h-5 w-5 text-copper" strokeWidth={1.6} />
        </span>
        <div>
          <h3 className="font-serif text-2xl leading-tight text-cream sm:text-3xl">{scenario.title}</h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-cream/70">{scenario.description}</p>
          <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-copper transition-all group-hover:gap-2.5">
            {isMangal ? "Kişi sayına göre hesapla" : `${scenario.products?.length} ürün · Sofrayı Hazırla`} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
};

export const ScenarioSection = () => {
  const { data: scenarios = [] } = useScenarios();
  const { addItems, setIsOpen, isInList } = useOrderList();
  const [active, setActive] = useState(null);

  const openScenario = (scenario) => {
    trackEvent("scenario_open", { key: scenario.key, label: scenario.title });
    setActive(scenario);
  };

  const addAll = (scenario) => {
    const items = scenario.products.map((p) => ({ slug: p.slug, name: p.name, image: p.image, price: p.price ?? null, unit: p.unit || "kg", quantity: "1 kg" }));
    addItems(items);
    toast.success(`${scenario.title} sofrası listene eklendi`, {
      description: `${items.length} ürün sipariş listene aktarıldı. Miktarları listeden ayarlayabilirsin.`,
    });
    setActive(null);
    setIsOpen(true);
  };

  const addOne = (p) => {
    addItems([{ slug: p.slug, name: p.name, image: p.image, price: p.price ?? null, unit: p.unit || "kg", quantity: "1 kg" }]);
    toast.success(`${p.name} listene eklendi`);
  };

  if (!scenarios.length) return null;

  const isMangal = active?.key === "mangal";

  return (
    <section id="senaryolar" data-testid="scenario-section" className="bg-charcoal scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <SectionHeading
          tone="dark"
          eyebrow="Sofra Senaryoları"
          title="Bu Akşam Sofrada Ne Var?"
          description="Sofranızı seçin; ilgili etleri sizin için bir araya getirelim ve tek dokunuşla sipariş listenize ekleyelim."
          className="mb-12"
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {scenarios.map((s) => (
            <ScenarioTile key={s.key} scenario={s} onOpen={openScenario} />
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="scenario-dialog"
          className="max-h-[90vh] gap-0 overflow-y-auto border-copper/30 bg-ink p-0 text-cream sm:max-w-2xl"
        >
          {active && (
            <>
              <div className="border-b border-dashed border-copper/30 bg-stone-light px-6 py-5">
                <DialogTitle className="font-serif text-3xl text-cream">{active.title}</DialogTitle>
                <DialogDescription className="mt-1.5 text-sm leading-relaxed text-cream/70">
                  {active.description}
                </DialogDescription>
              </div>

              {isMangal ? (
                <MangalPlanner onAdded={() => setActive(null)} />
              ) : (
                <>
                  <div className="space-y-2.5 px-4 py-5 sm:px-6">
                    {active.products.map((p) => {
                      const inList = isInList(p.slug);
                      return (
                        <div
                          key={p.slug}
                          data-testid={`scenario-product-${p.slug}`}
                          className="flex items-center gap-4 border border-dashed border-copper/20 bg-stone p-3"
                        >
                          <img src={p.image} alt={p.name} className="h-16 w-16 flex-shrink-0 object-cover" />
                          <div className="min-w-0 flex-1">
                            <h4 className="font-serif text-xl leading-tight text-cream">{p.name}</h4>
                            <p className="line-clamp-1 text-xs text-cream/55">{p.short_description}</p>
                            {p.price != null && (
                              <span className="mt-1 block font-mono text-xs font-bold text-copper">
                                {formatPrice(p.price)} <span className="text-cream/40">/ {p.unit || "kg"}</span>
                              </span>
                            )}
                          </div>
                          <button
                            data-testid={`scenario-add-one-${p.slug}`}
                            onClick={() => addOne(p)}
                            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-sm border border-copper/40 px-3 py-2 font-mono text-[11px] uppercase tracking-wide text-copper transition-colors hover:bg-copper hover:text-charcoal"
                          >
                            {inList ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                            {inList ? "Listede" : "Ekle"}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="sticky bottom-0 border-t border-dashed border-copper/30 bg-stone-light px-4 py-4 sm:px-6">
                    <button
                      data-testid={`scenario-add-all-${active.key}`}
                      onClick={() => addAll(active)}
                      className="flex w-full items-center justify-center gap-2.5 rounded-sm bg-bordo px-5 py-3.5 text-base font-bold uppercase tracking-wide text-cream transition-colors hover:bg-bordo-dark"
                    >
                      <ListPlus className="h-5 w-5" /> Tümünü Sipariş Listeme Ekle
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ScenarioSection;
